

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Quote,
  Star,
  CheckCircle,
  User,
} from "lucide-react";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { testimonialsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface Testimonial {
  id: number;
  name: string;
  content: string;
  rating: number;
  event_type?: string;
  date?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function TestimonialForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<Testimonial>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [rating, setRating] = useState(initialData?.rating || 5);
  const [eventType, setEventType] = useState(initialData?.event_type || '');
  const [date, setDate] = useState(() => {
    if (initialData?.date) {
      // Handle both string and Date object formats
      const dateStr = typeof initialData.date === 'string' ? initialData.date : new Date(initialData.date).toISOString();
      return dateStr.split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  });
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      content,
      rating,
      event_type: eventType,
      date,
      is_active: isActive,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Client Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          placeholder="Enter client name"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Testimonial Content *</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none min-h-[100px]"
          placeholder="Enter testimonial content"
          required
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Rating</label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              className={`p-1 ${star <= rating ? 'text-amber-500' : 'text-stone-300'}`}
            >
              <Star className="w-5 h-5 fill-current" />
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Event Type</label>
        <input
          type="text"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          placeholder="e.g., Wedding, Corporate"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Date *</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            required
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Active</label>
          <button
            type="button"
            onClick={() => setIsActive(!isActive)}
            className={`w-full px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              isActive ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {isActive ? "Yes" : "No"}
          </button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Testimonial
        </button>
      </div>
    </form>
  );
}

export default function TestimonialsManager() {
  const { showToast } = useToast();

  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTestimonial, setDeletingTestimonial] = useState<Testimonial | null>(null);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const response = await testimonialsApi.list();
      const data = response.data?.data || [];
      setTestimonials(data);
    } catch (error) {
      showToast('Failed to load testimonials', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTestimonials();
  }, []);

  const handleCreate = async (data: any) => {
    try {
      await testimonialsApi.create(data);
      showToast('Testimonial created successfully', 'success');
      setIsModalOpen(false);
      loadTestimonials();
    } catch (error) {
      showToast('Failed to create testimonial', 'error');
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editingTestimonial) return;
    try {
      await testimonialsApi.update(editingTestimonial.id, data);
      showToast('Testimonial updated successfully', 'success');
      setIsModalOpen(false);
      setEditingTestimonial(null);
      loadTestimonials();
    } catch (error) {
      showToast('Failed to update testimonial', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deletingTestimonial) return;
    try {
      await testimonialsApi.delete(deletingTestimonial.id);
      showToast('Testimonial deleted successfully', 'success');
      setDeleteModalOpen(false);
      setDeletingTestimonial(null);
      loadTestimonials();
    } catch (error) {
      showToast('Failed to delete testimonial', 'error');
    }
  };

  const handleToggleActive = async (testimonial: Testimonial) => {
    try {
      await testimonialsApi.update(testimonial.id, { is_active: !testimonial.is_active });
      showToast('Status updated successfully', 'success');
      loadTestimonials();
    } catch (error) {
      showToast('Failed to update status', 'error');
    }
  };

  const filteredTestimonials = useMemo(() => {
    if (!searchQuery) return testimonials;
    const q = searchQuery.toLowerCase();
    return testimonials.filter(t =>
      t.name.toLowerCase().includes(q) ||
      t.content.toLowerCase().includes(q)
    );
  }, [testimonials, searchQuery]);

  const stats = useMemo(
    () => ({
      total: testimonials.length,
      active: testimonials.filter(t => t.is_active).length,
      featured: testimonials.filter(t => t.is_active).length,
    }),
    [testimonials]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Active" value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <StatBadge label="Featured" value={stats.featured} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search testimonials..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditingTestimonial(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Testimonial
        </button>
      </div>

      {/* Testimonials List */}
      {filteredTestimonials.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Quote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No testimonials found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-white rounded-xl border border-stone-200 p-3 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-stone-800 text-sm">{testimonial.name}</p>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3 h-3 ${
                            i < testimonial.rating ? 'text-amber-500 fill-current' : 'text-stone-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-stone-600 text-xs line-clamp-2 mb-1">{testimonial.content}</p>
                  {testimonial.event_type && (
                    <p className="text-xs text-stone-400">Event: {testimonial.event_type}</p>
                  )}
                  {testimonial.date && (
                    <p className="text-xs text-stone-400">Date: {new Date(testimonial.date).toLocaleDateString()}</p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(testimonial)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      testimonial.is_active ? 'text-emerald-600 bg-emerald-50' : 'text-stone-400 bg-stone-50'
                    }`}
                    title={testimonial.is_active ? 'Active' : 'Inactive'}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setEditingTestimonial(testimonial);
                      setIsModalOpen(true);
                    }}
                    className="p-1.5 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => {
                      setDeletingTestimonial(testimonial);
                      setDeleteModalOpen(true);
                    }}
                    className="p-1.5 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTestimonial(null);
        }}
        title={editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
        size="md"
      >
        <TestimonialForm
          initialData={editingTestimonial || undefined}
          onSubmit={editingTestimonial ? handleUpdate : handleCreate}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingTestimonial(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingTestimonial(null);
        }}
        onConfirm={handleDelete}
        title="Delete Testimonial"
        message="Are you sure you want to delete this testimonial? This action cannot be undone."
        itemName={deletingTestimonial?.name}
      />
    </div>
  );
}
